/**
 * Apply notification seam.
 *
 * `useEditorStore.execCommand` is the one place every undoable tool apply passes
 * through, so it is where the undo bookkeeping belongs — but the bookkeeping needs
 * the active workflow step, and the workflow store already imports the editor
 * store. Importing it back would close a cycle.
 *
 * So this module holds no store imports at all. The editor store calls
 * `notifyToolApplied`; the real implementation registers itself from
 * `applyRecorderImpl`, which is free to import whatever it likes. Same pattern as
 * the replay engine's `setServerAnchorResolver`.
 *
 * @typedef {Object} ToolApplyEvent
 * @property {string} toolId
 * @property {number} durationMs        Geometry work only — excludes preview/UI time.
 * @property {number|null} vertexCount  Socket size the apply produced.
 * @property {object|null} prePayload   Pre-apply snapshot payload, for the uploader.
 * @property {Record<string, unknown>} params  Replay parameters, when the tool supplies them.
 */

/** @type {((event: ToolApplyEvent) => void) | null} */
let recorder = null;

/** @param {((event: ToolApplyEvent) => void) | null} fn */
export function setApplyRecorder(fn) {
  recorder = typeof fn === 'function' ? fn : null;
}

/**
 * Announce a completed apply. Never throws — a bookkeeping failure must not break
 * the apply the user just performed.
 *
 * @param {ToolApplyEvent} event
 */
export function notifyToolApplied(event) {
  if (!recorder || !event?.toolId) return;
  try {
    recorder(event);
  } catch (e) {
    console.warn('[ApplyRecorder] bookkeeping failed for', event.toolId, e);
  }
}

export function hasApplyRecorder() {
  return recorder != null;
}
